import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Plus, Trash2, CalendarOff } from "lucide-react";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { Panel, EmptyState, formatDateTime } from "@/components/admin/ui";

const emptyForm = (scope, userId) => ({ scope, team_id: "", user_id: scope === "user" ? userId || "" : "", category: "unavailable", start_at: "", end_at: "", notes: "" });

export const OfficeTimeOffTab = ({ meta, API }) => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const defaultScope = meta.scope.level === "org" ? "organization" : meta.scope.level === "team" ? "team" : "user";
  const [form, setForm] = useState(emptyForm(defaultScope, meta.scope.level === "self" ? meta.users[0]?.user_id : ""));
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/org/office/time-off`, { withCredentials: true })
      .then(({ data }) => setRows(data.time_off))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [API]);

  useEffect(() => { load(); }, [load]);

  const scopeOptions = [
    ...(meta.scope.level === "org" ? [{ v: "organization", l: "Organization-wide" }] : []),
    ...(meta.scope.level !== "self" ? [{ v: "team", l: "Team" }] : []),
    { v: "user", l: meta.scope.level === "self" ? "Myself" : "Individual" },
  ];

  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      await axios.post(`${API}/org/office/time-off`, {
        scope: form.scope,
        team_id: form.scope === "team" ? form.team_id || null : null,
        user_id: form.scope === "user" ? form.user_id || null : null,
        category: form.category,
        start_at: form.start_at,
        end_at: form.end_at,
        notes: form.notes || null,
      }, { withCredentials: true });
      setShowForm(false);
      setForm(emptyForm(defaultScope, meta.scope.level === "self" ? meta.users[0]?.user_id : ""));
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    setError("");
    try {
      await axios.delete(`${API}/org/office/time-off/${id}`, { withCredentials: true });
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const describeScope = (row) => {
    if (row.scope === "organization") return "Organization-wide";
    if (row.scope === "team") return `Team: ${meta.teams.find((t) => t.team_id === row.team_id)?.name || row.team_id}`;
    return `User: ${meta.users.find((u) => u.user_id === row.user_id)?.name || row.user_id}`;
  };

  return (
    <Panel
      title="Time Off & Blocks"
      subtitle="PTO, sick time, training, internal meetings and other blocks that remove availability."
      testId="office-time-off-tab"
      action={
        <button type="button" onClick={() => setShowForm((s) => !s)} data-testid="office-time-off-add-btn"
          className="flex items-center gap-1.5 rounded-xl border border-transparent bg-[#0052FF] px-3.5 py-2 text-xs font-bold text-white transition-colors duration-150 hover:bg-[#0040CC]">
          <Plus size={14} /> Add Time Off
        </button>
      }
    >
      {error && <p className="mb-3 text-sm font-semibold text-red-600 dark:text-red-400" data-testid="office-time-off-error">{error}</p>}

      {showForm && (
        <div className="mb-5 rounded-xl border border-slate-200 p-4 dark:border-white/10" data-testid="office-time-off-form">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">Scope</label>
              <select value={form.scope} onChange={(e) => setForm((f) => ({ ...f, scope: e.target.value }))} data-testid="office-time-off-scope-select"
                className="mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-white/15 dark:bg-[#0B1220] dark:text-white">
                {scopeOptions.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">Category</label>
              <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} data-testid="office-time-off-category-select"
                className="mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-white/15 dark:bg-[#0B1220] dark:text-white">
                {meta.time_off_categories.map((c) => <option key={c} value={c}>{meta.time_off_category_labels[c]}</option>)}
              </select>
            </div>
            {form.scope === "team" && (
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">Team</label>
                <select value={form.team_id} onChange={(e) => setForm((f) => ({ ...f, team_id: e.target.value }))} data-testid="office-time-off-team-select"
                  className="mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-white/15 dark:bg-[#0B1220] dark:text-white">
                  <option value="">Select Team…</option>
                  {meta.teams.map((t) => <option key={t.team_id} value={t.team_id}>{t.name}</option>)}
                </select>
              </div>
            )}
            {form.scope === "user" && meta.scope.level !== "self" && (
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">User</label>
                <select value={form.user_id} onChange={(e) => setForm((f) => ({ ...f, user_id: e.target.value }))} data-testid="office-time-off-user-select"
                  className="mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-white/15 dark:bg-[#0B1220] dark:text-white">
                  <option value="">Select User…</option>
                  {meta.users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">Start</label>
              <input type="datetime-local" value={form.start_at} onChange={(e) => setForm((f) => ({ ...f, start_at: e.target.value }))} data-testid="office-time-off-start-input"
                className="mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-white/15 dark:bg-[#0B1220] dark:text-white" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">End</label>
              <input type="datetime-local" value={form.end_at} onChange={(e) => setForm((f) => ({ ...f, end_at: e.target.value }))} data-testid="office-time-off-end-input"
                className="mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-white/15 dark:bg-[#0B1220] dark:text-white" />
            </div>
          </div>
          <div className="mt-3">
            <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">Notes</label>
            <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} rows={2} data-testid="office-time-off-notes-input"
              className="mt-1 w-full rounded-lg border border-slate-200 p-2.5 text-sm dark:border-white/15 dark:bg-[#0B1220] dark:text-white" />
          </div>
          <div className="mt-3">
            <button type="button" onClick={submit} disabled={saving || !form.start_at || !form.end_at} data-testid="office-time-off-submit-btn"
              className="rounded-xl border border-transparent bg-[#0052FF] px-3.5 py-2 text-xs font-bold text-white transition-colors duration-150 hover:bg-[#0040CC] disabled:cursor-not-allowed disabled:opacity-50">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}

      {!rows ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : rows.length === 0 ? (
        <EmptyState icon={CalendarOff} title="No time off recorded." message="Add PTO, sick time, training or other blocks that remove availability." testId="office-time-off-empty" />
      ) : (
        <div className="space-y-2" data-testid="office-time-off-list">
          {rows.map((r) => (
            <div key={r.time_off_id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-3 dark:border-white/10" data-testid={`office-time-off-row-${r.time_off_id}`}>
              <div>
                <p className="text-sm font-bold text-slate-900 dark:text-white">{meta.time_off_category_labels[r.category]} — {describeScope(r)}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{formatDateTime(r.start_at)} → {formatDateTime(r.end_at)}</p>
                {r.notes && <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{r.notes}</p>}
              </div>
              <button type="button" onClick={() => remove(r.time_off_id)} data-testid={`office-time-off-delete-${r.time_off_id}`}
                className="rounded-lg p-2 text-slate-400 transition-colors duration-200 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10">
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
};
